import { useEffect, useState } from "react";
import type { ReactNode } from "react";

// The one way anything in spark expands or collapses in place: the CSS
// grid-rows 0fr→1fr trick at --duration-base. Content stays mounted while
// closed (state survives a collapse) and is marked inert, so hidden controls
// can't be tabbed into or read by assistive tech.
//
// This is the animation mechanic alone - no chrome, no trigger. `Disclosure`
// composes it under its chevron header; anything with its own trigger (a
// "show YAML" button, an inline preview) wraps its body in <Collapse> instead
// of mounting/unmounting it, which is what makes opening glide instead of pop.
//
// Height animation needs overflow-hidden while moving (the 0fr state clips),
// but a permanently clipped body would cut off popovers and tooltips inside.
// So the clip is released once the open transition settled, exactly like
// Disclosure has always done.

export function Collapse({
  open,
  children,
  className = "",
  onClosed,
}: {
  open: boolean;
  children: ReactNode;
  className?: string;
  // Fires when the CLOSE transition has actually finished, not when `open`
  // flips to false. Callers that swap which instance holds the content (e.g.
  // one Collapse per grid row, content moves from row A to row B) need this
  // to know when it's safe to unmount A's children without cutting its
  // closing animation short.
  onClosed?: () => void;
}) {
  const [settled, setSettled] = useState(open);
  useEffect(() => {
    if (open) return;
    // Closed content is clipped immediately through `open`; reset the settled
    // marker on the next frame so a later opening stays clipped until its own
    // transition ends, without causing a synchronous effect re-render.
    const frame = window.requestAnimationFrame(() => setSettled(false));
    return () => window.cancelAnimationFrame(frame);
  }, [open]);
  const clip = !(open && settled);

  return (
    <div
      inert={!open}
      onTransitionEnd={(e) => {
        // Only this element's own grid-rows transition counts (body transitions bubble up).
        if (e.target !== e.currentTarget) return;
        if (open) setSettled(true);
        else onClosed?.();
      }}
      className={`grid transition-[grid-template-rows] duration-[var(--duration-base)] ${
        open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
      } ${className}`}
    >
      {/* min-w-0: als Grid-Item waere die Mindestbreite sonst die MIN-CONTENT-Breite des
          Inhalts. Eine breite Tabelle blaeht damit die Spalte auf, laeuft aus der Box heraus,
          und ihr eigenes overflow-x-auto scrollt nie (es ist ja nie zu schmal). Kam aus der
          Disclosure (06bb2299d) und gilt fuer jeden Collapse-Inhalt, nicht nur fuer sie. */}
      <div className={`min-w-0 ${clip ? "overflow-hidden" : ""}`}>{children}</div>
    </div>
  );
}
